import { defaultCategories, defaultFormOfOwnership } from '@/context/default';
import { ColumnEnum, Weights } from '@/enums';
import strings from '@/language/Swedish';
import { ColumnType } from '@/types/dataTypes';

const todaysDate = new Date();

const defaultSoldWeightParams = {
  construction_year_weight: Weights.none,
  contract_date_weight: Weights.medium,
  distance_weight: Weights.medium,
  floor_weight: Weights.none,
  floors_weight: Weights.none,
  living_area_weight: Weights.medium,
  monthly_fee_weight: Weights.none,
  rooms_weight: Weights.none,
  population_center_weight: Weights.none,
};

const defaultSoldRefParams = {
  construction_year_reference: null,
  floor_reference: null,
  floors_reference: null,
  living_area_reference: 78,
  monthly_fee_reference: null,
  rooms_reference: null,
  use_fpr_data: false
};

const defaultSoldFilterParams = {
  home_type: defaultCategories,
  construction_year_min: null,
  construction_year_max: null,
  shore_distance_min: null,
  shore_distance_max: null,
  contract_date_min: new Date(
    new Date().setFullYear(new Date().getFullYear() - 10)
  ) as Date,
  contract_date_max: new Date('null'),
  distance_center: true,
  error_code: false,
  excluded_sale_ids: [],
  ownership_type: [...defaultFormOfOwnership],
  limit: 15,
  owner_id: '',
  living_area_min: null,
  living_area_max: null,
  is_close_to_population_centre: true,
  is_new_construction: false,
  monthly_fee_min: null,
  monthly_fee_max: null,
  price_min: null,
  price_max: null,
  rooms_min: null,
  rooms_max: null,
  floor_min: null,
  floor_max: null,
  orgNr: false,
  firm: false,
  estate_agencies: null,
  use_fpr_data: true,
};

const resetSoldFilterParams = {
  home_type: defaultCategories,
  construction_year_min: 1850,
  shore_distance_min: 1,
  shore_distance_max: 4,
  construction_year_max: todaysDate.getFullYear(),
  contract_date_min: new Date(
    new Date().setFullYear(new Date().getFullYear() - 10)
  ) as Date,
  contract_date_max: new Date('null'),
  distance_center: true,
  error_code: false,
  excluded_sale_ids: [],
  ownership_type: [...defaultFormOfOwnership],
  limit: 15,
  owner_id: '',
  living_area_min: 0,
  living_area_max: 500,
  is_close_to_population_centre: true,
  is_new_construction: false,
  orgNr: false,
  firm: false,
  estate_agencies: null,
  monthly_fee_min: 0,
  monthly_fee_max: 12000,
  price_min: 0,
  price_max: 40,
  rooms_min: 0,
  rooms_max: 10,
  floor_min: 0,
  floor_max: 20,
  use_fpr_data: true,
};

const defaultSoldTableColumns: ColumnType[] = [
  {
    id: ColumnEnum.street,
    label: strings.table.columns.street,
    isVisible: true,
  },
  {
    id: ColumnEnum.contractDate,
    label: strings.table.columns.contractDate,
    isVisible: true,
  },
  {
    id: ColumnEnum.soldPrice,
    label: strings.table.columns.soldPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.livingArea,
    label: strings.table.columns.livingArea,
    isVisible: true,
  },
  { id: ColumnEnum.rooms, label: strings.table.columns.rooms, isVisible: true },
  {
    id: ColumnEnum.floorFloors,
    label: strings.table.columns.floorFloors,
    isVisible: true,
  },

  {
    id: ColumnEnum.plotArea,
    label: strings.table.columns.plotArea,
    isVisible: false,
  },
  {
    id: ColumnEnum.ancillaryArea,
    label: strings.table.columns.ancillaryArea,
    isVisible: false,
  },
  {
    id: ColumnEnum.soldPriceSqm,
    label: strings.table.columns.soldPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPrice,
    label: strings.table.columns.recountedPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPriceSqm,
    label: strings.table.columns.recountedPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.taxasionValue,
    label: strings.table.columns.taxationValue,
    isVisible: false,
  },
  {
    id: ColumnEnum.taxationYear,
    label: strings.table.columns.taxationYear,
    isVisible: false,
  },
  {
    id: ColumnEnum.priceTaxationRatio,
    label: strings.table.columns.priceTaxationRatio,
    isVisible: false,
  },
  {
    id: ColumnEnum.monthlyFee,
    label: strings.table.columns.monthlyFee,
    isVisible: true,
  },
  {
    id: ColumnEnum.monthlyFeeSqm,
    label: strings.table.columns.monthlyFeeSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.source,
    label: strings.table.columns.source,
    isVisible: false,
  },
  //adTable
  {
    id: ColumnEnum.adRegisteredDate,
    label: strings.table.columns.adRegisteredDate,
    isVisible: false,
  },
  {
    id: ColumnEnum.adDeleteDate,
    label: strings.table.columns.adDeleteDate,
    isVisible: false,
  },
  {
    id: ColumnEnum.adAdvertisingDays,
    label: strings.table.columns.adAvertisingDays,
    isVisible: false,
  },
  {
    id: ColumnEnum.adPrice,
    label: strings.table.columns.adPrice,
    isVisible: false,
  },
  {
    id: ColumnEnum.adPriceSqm,
    label: strings.table.columns.adPriceSqm,
    isVisible: false,
  },
  {
    id: ColumnEnum.firm,
    label: strings.table.columns.firm,
    isVisible: true,
  },
  {
    id: ColumnEnum.prospectUrl,
    label: strings.table.columns.prospectUrl,
    isVisible: true,
  },
  //realEstateTable
  {
    id: ColumnEnum.propertyUnitDesignation,
    label: strings.table.columns.propertyUnitDesignation,
    isVisible: false,
  },
  {
    id: ColumnEnum.constructionYear,
    label: strings.table.columns.constructionYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.valuationYear,
    label: strings.table.columns.valuationYear,
    isVisible: false,
  },
  {
    id: ColumnEnum.homeType,
    label: strings.table.columns.homeType,
    isVisible: false,
  },
  {
    id: ColumnEnum.ownershipType,
    label: strings.table.columns.ownershipType,
    isVisible: false,
  },
  {
    id: ColumnEnum.housingAssociationReportYear,
    label: strings.table.columns.housingAssociationReportYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.ownerName,
    label: strings.table.columns.ownerName,
    isVisible: true,
  },
  {
    id: ColumnEnum.ownerId,
    label: strings.table.columns.ownerId,
    isVisible: true,
  },
  {
    id: ColumnEnum.allabrfUrl,
    label: strings.table.columns.allabrfUrl,
    isVisible: true,
  },
  {
    id: ColumnEnum.allabolagUrl,
    label: strings.table.columns.allabolagUrl,
    isVisible: false,
  },
  {
    id: ColumnEnum.shoreDistanceType,
    label: strings.table.columns.shoreDistanceType,
    isVisible: false,
  },
  {
    id: ColumnEnum.waterSewerageType,
    label: strings.table.columns.waterSewerageType,
    isVisible: false,
  },
  {
    id: ColumnEnum.alterationYear,
    label: strings.table.columns.alterationYear,
    isVisible: false,
  },
  {
    id: ColumnEnum.standardPoints,
    label: strings.table.columns.standardPoints,
    isVisible: false,
  },
  {
    id: ColumnEnum.leaseholdFee,
    label: strings.table.columns.leaseholdFee,
    isVisible: false,
  },
  {
    id: ColumnEnum.leaseholdPeriod,
    label: strings.table.columns.leaseholdPeriod,
    isVisible: false,
  },
  {
    id: ColumnEnum.subdivisionType,
    label: strings.table.columns.subdivisionType,
    isVisible: false,
  },
  {
    id: ColumnEnum.balcony,
    label: strings.table.columns.balcony,
    isVisible: false,
  },
  {
    id: ColumnEnum.elevator,
    label: strings.table.columns.elevator,
    isVisible: false,
  },
];


const defaultSoldTableSaleInfoColumnsExcel: ColumnType[] = [
  {
    id: ColumnEnum.street,
    label: strings.table.columns.street,
    isVisible: true,
  },

  {
    id: ColumnEnum.contractDate,
    label: strings.table.columns.contractDate,
    isVisible: true,
  },
  {
    id: ColumnEnum.soldPrice,
    label: strings.table.columns.soldPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.livingArea,
    label: strings.table.columns.livingArea,
    isVisible: true,
  },
  { id: ColumnEnum.rooms, label: strings.table.columns.rooms, isVisible: true },
  {
    id: ColumnEnum.floor,
    label: strings.table.columns.floor,
    isVisible: true,
  },
  {
    id: ColumnEnum.floors,
    label: strings.table.columns.floors,
    isVisible: true,
  },

  {
    id: ColumnEnum.plotArea,
    label: strings.table.columns.plotArea,
    isVisible: true,
  },

  {
    id: ColumnEnum.ancillaryArea,
    label: strings.table.columns.ancillaryArea,
    isVisible: true,
  },
  {
    id: ColumnEnum.soldPriceSqm,
    label: strings.table.columns.soldPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPrice,
    label: strings.table.columns.recountedPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPriceSqm,
    label: strings.table.columns.recountedPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.taxasionValue,
    label: strings.table.columns.taxationValue,
    isVisible: true,
  },
  {
    id: ColumnEnum.taxationYear,
    label: strings.table.columns.taxationYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.monthlyFee,
    label: strings.table.columns.monthlyFee,
    isVisible: true,
  },
  {
    id: ColumnEnum.monthlyFeeSqm,
    label: strings.table.columns.monthlyFeeSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.priceTaxationRatio,
    label: strings.table.columns.priceTaxationRatio,
    isVisible: true,
  },
  {
    id: ColumnEnum.source,
    label: strings.table.columns.source,
    isVisible: true,
  },
];

const defaultSoldTableSaleInfoColumns: ColumnType[] = [
  {
    id: ColumnEnum.street,
    label: strings.table.columns.street,
    isVisible: true,
  },

  {
    id: ColumnEnum.contractDate,
    label: strings.table.columns.contractDate,
    isVisible: true,
  },
  {
    id: ColumnEnum.soldPrice,
    label: strings.table.columns.soldPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.livingArea,
    label: strings.table.columns.livingArea,
    isVisible: true,
  },
  { id: ColumnEnum.rooms, label: strings.table.columns.rooms, isVisible: true },
  {
    id: ColumnEnum.floorFloors,
    label: strings.table.columns.floorFloors,
    isVisible: true,
  },

  {
    id: ColumnEnum.plotArea,
    label: strings.table.columns.plotArea,
    isVisible: true,
  },

  {
    id: ColumnEnum.ancillaryArea,
    label: strings.table.columns.ancillaryArea,
    isVisible: true,
  },
  {
    id: ColumnEnum.soldPriceSqm,
    label: strings.table.columns.soldPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPrice,
    label: strings.table.columns.recountedPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPriceSqm,
    label: strings.table.columns.recountedPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.taxasionValue,
    label: strings.table.columns.taxationValue,
    isVisible: true,
  },
  {
    id: ColumnEnum.taxationYear,
    label: strings.table.columns.taxationYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.monthlyFee,
    label: strings.table.columns.monthlyFee,
    isVisible: true,
  },
  {
    id: ColumnEnum.monthlyFeeSqm,
    label: strings.table.columns.monthlyFeeSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.priceTaxationRatio,
    label: strings.table.columns.priceTaxationRatio,
    isVisible: true,
  },
  {
    id: ColumnEnum.source,
    label: strings.table.columns.source,
    isVisible: true,
  },
];

const defaultSoldTableAdInfoColumns: ColumnType[] = [
  {
    id: ColumnEnum.street,
    label: strings.table.columns.street,
    isVisible: true,
  },
  {
    id: ColumnEnum.adRegisteredDate,
    label: strings.table.columns.adRegisteredDate,
    isVisible: true,
  },
  {
    id: ColumnEnum.adDeleteDate,
    label: strings.table.columns.adDeleteDate,
    isVisible: true,
  },
  {
    id: ColumnEnum.adAdvertisingDays,
    label: strings.table.columns.adAvertisingDays,
    isVisible: true,
  },

  {
    id: ColumnEnum.adPrice,
    label: strings.table.columns.adPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.adPriceSqm,
    label: strings.table.columns.adPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.firm,
    label: strings.table.columns.firm,
    isVisible: true,
  },
  {
    id: ColumnEnum.prospectUrl,
    label: strings.table.columns.prospectUrl,
    isVisible: true,
  },
];

const defaultSoldCoOperative: ColumnType[] = [
  {
    id: ColumnEnum.street,
    label: strings.table.columns.street,
    isVisible: true,
  },
  {
    id: ColumnEnum.contractDate,
    label: strings.table.columns.contractDate,
    isVisible: true,
  },
  {
    id: ColumnEnum.soldPrice,
    label: strings.table.columns.soldPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.livingArea,
    label: strings.table.columns.livingArea,
    isVisible: true,
  },
  { id: ColumnEnum.rooms, label: strings.table.columns.rooms, isVisible: true },
  {
    id: ColumnEnum.floorFloors,
    label: strings.table.columns.floorFloors,
    isVisible: true,
  },
  {
    id: ColumnEnum.constructionYear,
    label: strings.table.columns.constructionYear,
    isVisible: true,
  },
    {
    id: ColumnEnum.housingAssociationReportYear,
    label: strings.table.columns.housingAssociationReportYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.soldPriceSqm,
    label: strings.table.columns.soldPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPrice,
    label: strings.table.columns.recountedPrice,
    isVisible: true,
  },
  {
    id: ColumnEnum.recountedPriceSqm,
    label: strings.table.columns.recountedPriceSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.monthlyFee,
    label: strings.table.columns.monthlyFee,
    isVisible: true,
  },
  {
    id: ColumnEnum.monthlyFeeSqm,
    label: strings.table.columns.monthlyFeeSqm,
    isVisible: true,
  },
  {
    id: ColumnEnum.ownerId,
    label: strings.table.columns.ownerId,
    isVisible: false,
  },
  {
    id: ColumnEnum.ownerName,
    label: strings.table.columns.ownerName,
    isVisible: false,
  },
  {
    id: ColumnEnum.allabrfUrl,
    label: strings.table.columns.allabrfUrl,
    isVisible: true,
  },
  {
    id: ColumnEnum.firm,
    label: strings.table.columns.firm,
    isVisible: true,
  },
  {
    id: ColumnEnum.prospectUrl,
    label: strings.table.columns.prospectUrl,
    isVisible: true,
  },
];

const defaultSoldLittleHouse: ColumnType[] = [
  {
    id: ColumnEnum.street,
    label: strings.table.columns.street,
    isVisible: false,
  },
  {
    id: ColumnEnum.contractDate,
    label: strings.table.columns.contractDate,
    isVisible: false,
  },
  {
    id: ColumnEnum.soldPrice,
    label: strings.table.columns.soldPrice,
    isVisible: false,
  },
  {
    id: ColumnEnum.livingArea,
    label: strings.table.columns.livingArea,
    isVisible: false,
  },
  {
    id: ColumnEnum.rooms,
    label: strings.table.columns.rooms,
    isVisible: false,
  },
  {
    id: ColumnEnum.plotArea,
    label: strings.table.columns.plotArea,
    isVisible: false,
  },
  {
    id: ColumnEnum.ancillaryArea,
    label: strings.table.columns.ancillaryArea,
    isVisible: false,
  },
  {
    id: ColumnEnum.soldPriceSqm,
    label: strings.table.columns.soldPriceSqm,
    isVisible: false,
  },
  {
    id: ColumnEnum.recountedPrice,
    label: strings.table.columns.recountedPrice,
    isVisible: false,
  },
  {
    id: ColumnEnum.recountedPriceSqm,
    label: strings.table.columns.recountedPriceSqm,
    isVisible: false,
  },
  {
    id: ColumnEnum.taxasionValue,
    label: strings.table.columns.taxationValue,
    isVisible: false,
  },
  {
    id: ColumnEnum.priceTaxationRatio,
    label: strings.table.columns.priceTaxationRatio,
    isVisible: false,
  },
  {
    id: ColumnEnum.homeType,
    label: strings.table.columns.homeType,
    isVisible: false,
  },
  {
    id: ColumnEnum.ownershipType,
    label: strings.table.columns.ownershipType,
    isVisible: false,
  },
  {
    id: ColumnEnum.firm,
    label: strings.table.columns.firm,
    isVisible: false,
  },
  {
    id: ColumnEnum.prospectUrl,
    label: strings.table.columns.prospectUrl,
    isVisible: false,
  },
];

const defaultSoldTableRealEstateColumns: ColumnType[] = [
  {
    id: ColumnEnum.street,
    label: strings.table.columns.street,
    isVisible: true,
  },
  {
    id: ColumnEnum.propertyUnitDesignation,
    label: strings.table.columns.propertyUnitDesignation,
    isVisible: true,
  },
  {
    id: ColumnEnum.valuationYear,
    label: strings.table.columns.valuationYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.constructionYear,
    label: strings.table.columns.constructionYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.homeType,
    label: strings.table.columns.homeType,
    isVisible: true,
  },
  {
    id: ColumnEnum.ownershipType,
    label: strings.table.columns.ownershipType,
    isVisible: true,
  },
  {
    id: ColumnEnum.housingAssociationReportYear,
    label: strings.table.columns.housingAssociationReportYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.ownerName,
    label: strings.table.columns.ownerName,
    isVisible: true,
  },
  {
    id: ColumnEnum.ownerId,
    label: strings.table.columns.ownerId,
    isVisible: true,
  },
  {
    id: ColumnEnum.allabrfUrl,
    label: strings.table.columns.allabrfUrl,
    isVisible: true,
  },
  {
    id: ColumnEnum.allabolagUrl,
    label: strings.table.columns.allabolagUrl,
    isVisible: true,
  },
  {
    id: ColumnEnum.shoreDistanceType,
    label: strings.table.columns.shoreDistanceType,
    isVisible: true,
  },
  {
    id: ColumnEnum.waterSewerageType,
    label: strings.table.columns.waterSewerageType,
    isVisible: true,
  },
  {
    id: ColumnEnum.alterationYear,
    label: strings.table.columns.alterationYear,
    isVisible: true,
  },
  {
    id: ColumnEnum.standardPoints,
    label: strings.table.columns.standardPoints,
    isVisible: true,
  },
  {
    id: ColumnEnum.leaseholdFee,
    label: strings.table.columns.leaseholdFee,
    isVisible: true,
  },
  {
    id: ColumnEnum.leaseholdPeriod,
    label: strings.table.columns.leaseholdPeriod,
    isVisible: true,
  },
  {
    id: ColumnEnum.subdivisionType,
    label: strings.table.columns.subdivisionType,
    isVisible: true,
  },
  {
    id: ColumnEnum.balcony,
    label: strings.table.columns.balcony,
    isVisible: false,
  },
  {
    id: ColumnEnum.elevator,
    label: strings.table.columns.elevator,
    isVisible: false,
  },
];

export {
  defaultSoldTableColumns,
  defaultSoldWeightParams,
  defaultSoldFilterParams,
  defaultSoldRefParams,
  defaultSoldTableAdInfoColumns,
  defaultSoldTableSaleInfoColumns,
  defaultSoldTableRealEstateColumns,
  defaultSoldTableSaleInfoColumnsExcel,
  defaultSoldCoOperative,
  defaultSoldLittleHouse,
  resetSoldFilterParams,
};
